import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { getLochBySlug } from '@/app/lib/lochs';
import { PlaceHolderImages } from '@/lib/placeholder-images';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LocationDetails } from '@/components/locations/LocationDetails';
import { MapPin, Star, Waves, CheckCircle, PlusCircle, Loader2 } from 'lucide-react';
import { AddToItinerary } from '@/components/locations/AddToItinerary';
import type { Loch } from '@/app/lib/types';

export default function LocationDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [location, setLocation] = useState<Loch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    async function fetchData() {
      if (!slug) return;
      try {
        const data = await getLochBySlug(slug);
        if (data) {
          setLocation(data);
        } else {
          setError(true);
        }
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [slug]);

  if (loading) {
    return <div className="flex justify-center items-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  if (error || !location) {
    return <Navigate to="/locations" replace />;
  }

  const image = PlaceHolderImages.find((img) => img.id === location.imageId);

  return (
    <div className="container mx-auto max-w-6xl">
      <div className="relative mb-4 sm:mb-6 md:mb-8 h-48 sm:h-56 md:h-80 lg:h-96 w-full rounded-md sm:rounded-lg overflow-hidden shadow-md sm:shadow-lg">
        {image && (
          <img
            src={image.imageUrl}
            alt={image.description}
            className="object-cover w-full h-full"
            data-ai-hint={image.imageHint}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 p-3 sm:p-4 md:p-6 text-white">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight drop-shadow-md">{location.name}</h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl font-medium flex items-center gap-2 mt-1">
            <MapPin className="h-4 w-4 sm:h-5 sm:w-5" />
            {location.region}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
        <div className="md:col-span-2">
          <LocationDetails location={location} />
        </div>
        <div className="space-y-3 sm:space-y-4 md:space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>At a Glance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-2"><Star className="h-4 w-4" /> Rating</span>
                <Badge variant="default" className="gap-1 pl-2"><Star className="h-3 w-3 fill-white" />{location.rating.toFixed(1)}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-2"><Waves className="h-4 w-4" /> Water Quality</span>
                <Badge variant="secondary">{location.water_quality}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-2"><CheckCircle className="h-4 w-4" /> Access</span>
                <span className="font-medium text-right">{location.access}</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PlusCircle className="h-5 w-5 text-primary" />
                Add to Itinerary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AddToItinerary location={location} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
